"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { buildSubmitHref, type SubmitContext } from "@/lib/submit-context";

type ShareCostStickyBarProps = {
  submitContext: SubmitContext;
};

/**
 * Server-rendered height placeholder. Reserves the same vertical space as
 * ShareCostStickyBar so that switching to `fixed` positioning does not
 * cause layout shift (CLS). Render this immediately before the bar's
 * usage site on `hasSyntheticOnly` pages.
 */
export function ShareCostStickyBarPlaceholder() {
  // h-[52px] matches py-3 (24px) + text line (~20px) + border (1px) = ~45-52px.
  // Using a rounded value that covers the rendered bar height on all viewports.
  return <div className="h-[52px] w-full" aria-hidden="true" />;
}

export function ShareCostStickyBar({ submitContext }: ShareCostStickyBarProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const href = buildSubmitHref(submitContext);

  return (
    <div
      className="fixed top-[64px] left-0 z-40 w-full border-b border-amber-200 bg-amber-50 px-4 py-3 shadow-sm"
      role="banner"
      aria-label="Share your cost"
    >
      <div className="container max-w-7xl flex items-center justify-between gap-4">
        <p className="text-sm font-semibold text-amber-900">
          We don&apos;t have direct patient reports for this procedure yet. Be the first to share what you paid.
        </p>
        <div className="flex items-center gap-3 shrink-0">
          <Button
            asChild
            variant="tertiary"
            size="sm"
            className="h-9 rounded-xl border-amber-400 bg-white text-amber-900 hover:bg-amber-100 hover:border-amber-500"
          >
            <Link href={href}>
              Share what you paid
              <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </Button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-amber-700 hover:bg-amber-100 transition-colors"
            aria-label="Dismiss"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
